import { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { formatDuration } from '../progressUtils';

// DAS-1699: Removed editingConfigId parameter - each run creates its own config snapshot
export const useBacktestRun = (callAction, config, selectedPortfolio, validateConfiguration, setConfigValidationErrors, setConfigOpen) => {
  const [loading, setLoading] = useState(false);
  const [runInfo, setRunInfo] = useState(null);
  const [runMeta, setRunMeta] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [runPct, setRunPct] = useState(0);
  const [statusMessage, setStatusMessage] = useState(null); // DAS-1685: Finalization phase feedback
  const [pollId, setPollId] = useState(null);
  const [runStartTimestamp, setRunStartTimestamp] = useState(null);
  const [elapsedLabel, setElapsedLabel] = useState(null);
  const [etaLabel, setEtaLabel] = useState(null);
  const [abortRunLoading, setAbortRunLoading] = useState(false);

  // Callbacks for when results are ready (to be set by parent)
  const onResultsReadyRef = useRef(null);

  const setOnResultsReady = (callback) => {
    onResultsReadyRef.current = callback;
  };

  // Poll for job status
  useEffect(() => {
    let intervalId = null;
    if (pollId) {
      intervalId = setInterval(async () => {
        await onStatus(true, pollId); // Silent poll
      }, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  // Timer for elapsed duration
  useEffect(() => {
    let timer = null;
    if (runStatus === 'running') {
      const updateElapsed = () => {
        if (runStartTimestamp) {
          const diff = Date.now() - runStartTimestamp;
          setElapsedLabel(formatDuration(diff));
        }
      };
      updateElapsed();
      timer = setInterval(updateElapsed, 1000);
    } else {
      setElapsedLabel(null);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [runStatus, runStartTimestamp]);

  const onRun = async () => {
    console.log('onRun started');
    console.log('selectedPortfolio:', selectedPortfolio);
    
    // Ensure start portfolio is set in config
    const finalStartPortfolioId = selectedPortfolio?.id || config.start_portfolio_id;
    const finalConfig = { ...config, start_portfolio_id: finalStartPortfolioId };
    console.log('finalConfig:', finalConfig);
    
    const errors = validateConfiguration(finalConfig);
    setConfigValidationErrors(errors);
    
    if (errors.length > 0) {
      console.log('Validation errors:', errors);
      setConfigOpen(true); // Open drawer to show errors
      return;
    }

    setLoading(true);
    setRunStatus(null);
    setRunPct(0);
    
    try {
        console.log('Calling run_backtest...');
        // DAS-1699: ALWAYS create a new configuration snapshot for each run
        // This ensures each run has a unique config_id for proper auditing
        // (Previously we reused editingConfigId, which caused all runs to share the same config)
        const payload = { ...finalConfig };
        // NOTE: We intentionally do NOT pass configuration_id here
        // Each run creates its own config snapshot in the database
        const res = await callAction('run_backtest', payload);
        console.log('run_backtest response:', res);
        
        if (res?.success) {
          // Check if res.data exists and has run_id
          if (res.data && res.data.run_id) {
            const { run_id, job_id } = res.data;
            message.success(`Run started: #${run_id}`);
            setRunInfo({ run_id, job_id });
            setPollId(run_id);
            setRunStartTimestamp(Date.now());
          } else if (res.data && res.data.error) {
            console.error('Backend error:', res.data.error);
            message.error(`Run failed: ${res.data.error}`);
          } else {
            console.error('Missing run_id in response data:', res.data);
            message.error('Run started but ID is missing');
          }
        } else {
          console.error('Run failed success check. Res:', res);
          message.error(res?.error || 'Run request failed');
        }
    } catch (e) {
        console.error('Exception in onRun:', e);
    } finally {
        setLoading(false);
    }
  };

  const onStatus = async (silent = false, overrideRunId = null) => {
    const rId = overrideRunId || runInfo?.run_id;
    if (!rId) return;
    const res = await callAction('get_run_status', { run_id: rId }, { useLoader: !silent });
    if (res?.success) {
      // DAS-1649 Fix: get_run_status returns { run: {...}, job: {...} }
      const runData = res.data.run || {};
      const status = runData.status;
      const pct_complete = runData.progress_pct;
      const meta = runData.progress_details;
      const statusMsg = runData.status_message; // DAS-1685: Finalization phase feedback
      
      setRunStatus(status);
      setRunPct(pct_complete || 0);
      setRunMeta(meta);
      setStatusMessage(statusMsg || null); // DAS-1685
      
      // Update ETA
      if (status === 'running' && pct_complete > 5 && runStartTimestamp) {
        const elapsedMs = Date.now() - runStartTimestamp;
        const estimatedTotalMs = (elapsedMs / pct_complete) * 100;
        const remainingMs = estimatedTotalMs - elapsedMs;
        setEtaLabel(formatDuration(remainingMs));
      } else {
        setEtaLabel(null);
      }

      if (status === 'completed' || status === 'failed' || status === 'aborted') {
        setPollId(null);
        setRunStartTimestamp(null); // Stop timer
        if (status === 'completed') {
          if (!silent) message.success('Run completed!');
          if (onResultsReadyRef.current) onResultsReadyRef.current(rId);
        } else {
          if (!silent) message.error(`Run ${status}`);
        }
      }
    }
  };

  const handleAbortRun = async () => {
    if (!runInfo?.run_id) return;
    setAbortRunLoading(true);
    try {
      const res = await callAction('abort_run', { run_id: runInfo.run_id });
      if (res?.success) {
        message.info('Run aborted');
        setRunStatus('aborted');
      } else {
        message.error(res?.error || 'Failed to abort run');
      }
    } catch (e) {
      message.error(`Failed to abort run: ${e.message}`);
    } finally {
      setAbortRunLoading(false);
    }
  };

  return {
    loading,
    setLoading,
    runInfo,
    setRunInfo,
    runMeta,
    setRunMeta,
    runStatus,
    setRunStatus,
    runPct,
    setRunPct,
    statusMessage, // DAS-1685: Finalization phase feedback
    pollId,
    setPollId,
    runStartTimestamp,
    setRunStartTimestamp,
    elapsedLabel,
    etaLabel,
    abortRunLoading,
    onRun,
    handleAbortRun,
    setOnResultsReady
  };
};
